/**
 * Candidates whose interview link expired before they booked.
 *
 * Lifted OUT of ExpiredLinksSection so the SLA tab can do two things with one
 * fetch: render each entry inside the role card it belongs to, and count them on
 * that role's chip.
 *
 * ── Why the grouping lives here ─────────────────────────────────────────────
 *
 * The list used to render as a single block above the role accordions, and was
 * reported as easy to miss entirely (Theresa, Sep 2026): a candidate sat there
 * under a role name that did not match any card on the page, so it read as an
 * unrelated widget rather than as work on one of her roles. The name mismatch is
 * real and not a bug in either place — the expired-link row names the interview
 * link's CANDIDATE-FACING `jobTitle` ("Test Role"), while a role card names the
 * Lever posting ("Test Opportunity _ for automation"). Matching on `postingId`
 * is what lets the row live under the card and inherit its title, so the two
 * names can never disagree again.
 *
 * Entries whose posting is on NO card keep their own block — a role the
 * recruiter does not own, or one with no SLA items at all, still has a candidate
 * holding a dead link, and dropping them to tidy the layout would lose real work.
 */
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchExpiredUnbooked, type ExpiredInvite } from '../../services/interviewLinksApi';

export interface ExpiredLinks {
  /** postingId → its expired invites. Only postings that have any. */
  byPosting: Map<string, ExpiredInvite[]>;
  /** Entries that match no role on the board — rendered on their own. */
  orphans: ExpiredInvite[];
  isPending: boolean;
  refetch: () => void;
}

export function useExpiredLinks(owner: string | undefined, postingIds: string[]): ExpiredLinks {
  const { data, isPending, refetch } = useQuery({
    queryKey: ['expired-unbooked', owner || ''],
    queryFn: () => fetchExpiredUnbooked(owner),
  });

  // A stable key, so a fresh array on every render does not re-group.
  const key = useMemo(() => [...postingIds].sort().join(','), [postingIds]);

  return useMemo(() => {
    const known = new Set(key ? key.split(',') : []);
    const byPosting = new Map<string, ExpiredInvite[]>();
    const orphans: ExpiredInvite[] = [];
    for (const item of data ?? []) {
      const posting = `${item.postingId || ''}`.trim();
      // No posting id at all is an orphan too: without one there is no card to
      // put it under, and guessing from the role NAME is exactly the mismatch
      // this grouping exists to remove.
      if (posting && known.has(posting)) {
        const list = byPosting.get(posting);
        if (list) list.push(item);
        else byPosting.set(posting, [item]);
      } else orphans.push(item);
    }
    return { byPosting, orphans, isPending, refetch: () => void refetch() };
  }, [data, key, isPending, refetch]);
}
